import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SttFormSwitch, SttLabelTitle } from '../all';

type Privilege = {
    id: string;
    name: string;
    description?: string;
    active: boolean;
    isDefaultOnSC?: boolean;
};

type Props = {
    privileges: Privilege[];
    onToggle: (params: { id: string; value: boolean }) => void;
    title: string;
    useHighlight?: boolean;
    classes?: {
        maxWidthList?: string;
        highlight?: string;
    } | null;
};

/**
 * Styled vertical list of privileges
 *
 *     * Privileges[] privileges - list of objects as obtained from the API (or similar)
 *     * Function onToggle({id, value}) - callback for each privilege row's switch
 */

export function SttPrivilegeList({
    privileges,
    onToggle,
    title,
    useHighlight = false,
    classes = null,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [option, setOption] = useState<number | null>(null);

    return (
        <div>
            <Box
                pt={1}
                pb={1}
                mb={1}
                className={classes?.maxWidthList ? classes.maxWidthList : ''}
                display="flex"
                justifyContent="space-between"
                borderBottom="1px solid #f0f0f0"
            >
                <Box flex={1}>
                    <Typography style={{ fontSize: 20 }} variant="caption">
                        {title}
                    </Typography>
                </Box>
                <Box
                    minWidth={200}
                    flex={0}
                    display="flex"
                    justifyContent="center"
                >
                    <Typography style={{ fontSize: 20 }} variant="caption">
                        {t('labels.status')}
                    </Typography>
                </Box>
            </Box>
            <div>
                {privileges.map((privilege: any, index: any) => (
                    <Box
                        mb={2}
                        className={
                            classes?.maxWidthList ? classes.maxWidthList : ''
                        }
                        key={privilege.id}
                        display="flex"
                        onMouseEnter={() =>
                            useHighlight ? setOption(index) : null
                        }
                        onMouseLeave={() =>
                            useHighlight ? setOption(null) : null
                        }
                    >
                        <Box
                            flex={1}
                            className={
                                option === index &&
                                useHighlight &&
                                classes?.highlight
                                    ? classes.highlight
                                    : undefined
                            }
                        >
                            <SttLabelTitle>{t(privilege.name)}</SttLabelTitle>
                            <Typography color="textSecondary" variant="body2">
                                {privilege.description
                                    ? t(privilege.description)
                                    : ''}
                            </Typography>
                        </Box>
                        <Box
                            minWidth={200}
                            flex={0}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            {privilege.isDefaultOnSC ? (
                                <Typography variant="body1">
                                    {t('sc.groups.privileges.activeDefault')}
                                </Typography>
                            ) : (
                                <SttFormSwitch
                                    color="primary"
                                    name={privilege.id}
                                    checked={privilege.active}
                                    onChange={({
                                        name,
                                        value,
                                    }: {
                                        name: string | undefined;
                                        value: unknown;
                                    }) => {
                                        if (!name) return;
                                        onToggle({
                                            id: name,
                                            value: value as boolean,
                                        });
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                ))}
            </div>
        </div>
    );
}
